import { useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import { CatalogDispatchContext, UxContext, CartContext } from '../store/CatalogContext';
import ViewCartBtn from './ViewCartBtn';
import useBrand from '../hooks/useBrand';
import getCartUrl from '../utils/getCartUrl';
import CartWidgetItems from './CartWidgetItems';
import cookies from '../utils/cookies';
import { useCartActions } from '../hooks/useCartActions';
import { formatCurrency } from '../utils/formatCurrency';
import { getDisplayPrice, shouldIncludeFees } from '../utils/amountHelper';

export default function CartWidget() {
  const brand = useBrand();
  const domain = brand?.domain;

  const catalogDispatch = useContext(CatalogDispatchContext);
  const ux = useContext(UxContext);
  const cart = useContext(CartContext);

  // Needs to update each time cart widget is shown to pick up latest cartId
  const cartUrl = useMemo(() => getCartUrl(domain), [domain, ux.showCartWidget]);

  const { getCart } = useCartActions({ catalogDispatch });

  // Fetch cart if cartId cookie exists
  useEffect(() => {
    const controller = new AbortController();

    const fetchCart = () => {
      const cartId = cookies.get('cartId');
      if (cartId) {
        getCart(cartId, void 0, controller);
        sessionStorage.removeItem('viewedCart'); // reset viewedCart flag on fetch
      }
      else {
        sessionStorage.removeItem('localCart'); // ensure that localCart is cleared if no cartId cookie exists
      }
    };

    fetchCart();

    const refetchCartOnPageShow = () => {
      const viewedCart = sessionStorage.getItem('viewedCart') === 'true';
      if (!viewedCart) return;
      fetchCart();
      sessionStorage.removeItem('viewedCart'); // reset viewedCart flag on fetch
    };

    // adding event listener to handle back/forward cache in browsers
    window.addEventListener('pageshow', () => refetchCartOnPageShow());

    return () => {
      controller.abort();

      // remove event listener when component unmounts
      window.removeEventListener('pageshow', () => refetchCartOnPageShow());
    };
  }, [getCart]);

  const cartProducts = cart?.cartProducts || [];

  // Compute cartItems, total, savings, coupons in a single pass
  const { cartItems, total, savings, appliedCoupons } = useMemo(() => {
    const products = cartProducts;
    const items = [];
    let runningTotal = 0;
    let runningSavings = 0;
    const coupons = new Set<string>();

    for (const product of products) {
      items.push(<CartWidgetItems product={product} key={product.productId} />);

      // upsells
      const upsellTotal = product.upSellProducts.reduce((acc, upsell) => {
        if (upsell.isSelected) {
          // savings from upsell
          if (upsell.discountSource === 'Coupon' && upsell.combinedDiscount > 0 && upsell.discountCode) {
            coupons.add(upsell.discountCode);
          }
          runningSavings += upsell.combinedDiscount * upsell.quantity;
          return acc + getDisplayPrice(upsell, true) * product.quantity;
        }
        return acc;
      }, 0);

      // savings from base product
      if (product.discountSource === 'Coupon' && product.combinedDiscount > 0 && product.discountCode) {
        coupons.add(product.discountCode);
      }
      runningSavings += product.combinedDiscount * product.quantity;

      // total
      if (shouldIncludeFees(product.productId)) {
        runningTotal += product.unitPriceWithDiscountAndFee * product.quantity + upsellTotal;
      } else {
        runningTotal += product.unitPriceWithDiscountAndFeeIfHidden * product.quantity + upsellTotal;
      }
    }

    return {
      cartItems: items.reverse(), // reverse here once
      total: runningTotal,
      savings: runningSavings,
      appliedCoupons: coupons.size > 0 ? Array.from(coupons).join(', ') : null,
    };
  }, [cartProducts]);

  const handleHideCart = useCallback(() => {
    catalogDispatch({
      type: 'UX',
      payload: {
        showCartWidget: false,
      },
    });
  }, [catalogDispatch]);

  // using cart widget ref to be able to watch for outside clicks
  const widgetRef = useRef<HTMLDivElement | null>(null);

  // Outside click handler using ref containment & pointer events
  useEffect(() => {
    if (!ux.showCartWidget) return;

    const handlePointerDown = (event: MouseEvent | PointerEvent) => {
      const target = event.target as Node;
      if (widgetRef.current?.contains(target) || ux.showBulkRates || ux.showFeeDescription) return;

      handleHideCart();
    };

    // using pointerdown because it immediately fires, and we want the widget hidden immediately
    document.addEventListener('pointerdown', handlePointerDown);

    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [ux.showCartWidget, ux.showBulkRates, ux.showFeeDescription, handleHideCart]);

  const handleViewCart = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();
    sessionStorage.setItem('viewedCart', 'true');
    window.location.href = cartUrl;
  };

  return (
    <>
      <ViewCartBtn isFloating={true} />
      <div id="cart-widget-container" className="position-fixed sticky-top p-2 p-sm-3" ref={widgetRef}>
        <div
          className={`card cart-widget border-light-subtle shadow-lg ${ux.showCartWidget ? 'show' : ''} ${
            cart?.isUpdating ? 'is-updating' : ''
          }`}
        >
          <div className="card-header bg-white border-bottom-0 py-0">
            <div className="d-flex align-items-center border-bottom py-2">
              <div className="me-auto text-reset d-flex align-items-center pe-3">
                <svg
                  className="align-middle me-2"
                  xmlns="http://www.w3.org/2000/svg"
                  height="24px"
                  viewBox="0 0 24 24"
                  width="24px"
                  fill="currentColor"
                >
                  <path d="M0 0h24v24H0V0z" fill="none"></path>
                  <path d="M15.55 13c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.37-.66-.11-1.48-.87-1.48H5.21l-.94-2H1v2h2l3.6 7.59-1.35 2.44C4.52 15.37 5.48 17 7 17h12v-2H7l1.1-2h7.45zM6.16 6h12.15l-2.76 5H8.53L6.16 6zM7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"></path>
                </svg>
                <h3 className="h5 fw-600 text-reset my-1">Order Summary</h3>
              </div>
              <button
                type="button"
                className="btn-close hide-cart-widget"
                aria-label="Close"
                onClick={handleHideCart}
              ></button>
            </div>
          </div>
          <div className="card-body pt-0 px-3">
            {cartItems.length === 0 ? (
              <ul className="cart-summary list-group list-group-flush bg-transparent w-100 m-0 px-0">
                <li className="list-group-item d-block fs-6 text-center pt-3 border-0 pb-0">
                  Your shopping cart is empty.
                  <br />
                  <button
                    className="btn btn-outline-secondary btn-sm hide-cart-widget d-inline-block mt-3 px-4"
                    type="button"
                    onClick={handleHideCart}
                  >
                    Continue Shopping
                  </button>
                </li>
              </ul>
            ) : (
              <ul className="cart-summary list-group list-group-flush bg-transparent w-100 m-0 px-0">
                {cartItems}
                <li className="list-group-item total fw-700 fs-6 px-0">
                  <div className="item-details">Total</div>
                  <div className="item-amount text-end">{formatCurrency(total)}</div>
                </li>
                {(savings ?? 0) > 0 && (
                  <li className="list-group-item savings-display">
                    <div className="item-details fw-700 text-success">
                      You Save
                      {appliedCoupons ? <div className="coupon-display fw-400">Coupon: {appliedCoupons}</div> : null}
                    </div>
                    <div className="item-amount text-end fw-700 text-success">{formatCurrency(savings)}</div>
                  </li>
                )}
              </ul>
            )}
          </div>
          {cartItems.length > 0 && (
            <div className="card-footer border-0 bg-transparent pt-0 pb-3 text-center">
              <a
                className="checkout-btn view-cart hide-cart-widget btn btn-lg text-white border-0 fw-600 w-100 py-2"
                href={cartUrl}
                onClick={handleViewCart}
              >
                <span className="btn-label">Checkout Now</span>
              </a>
              <button
                className="text-btn continue-shopping-btn hide-cart-widget mt-3 mb-1 p-0 border-0 bg-transparent"
                type="button"
                onClick={handleHideCart}
              >
                Continue Shopping
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
